import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { getAvailableFieldTypes } from '../../../../../core/config/field-types.config';
import { AccordionComponent } from '../../../../../shared/components/accordion/accordion.component';
import { RadioComponent } from '../../../../../shared/components/form-controls/radio/radio.component';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';
import { FieldDefinition } from '../../field-edit-base';

/**
 * Shared field type selector component
 *
 * Displays available field types as radio options in an accordion.
 * When user selects a different type, emits onFieldTypeSelected event.
 *
 * Field types are loaded from the centralized field-types.config.ts configuration.
 * This component displays only field types that have an editor component.
 *
 * Note: QR Code is NOT a form field type. The QrCodeFieldComponent exists in the app
 * for other purposes but is not available as a field type in the form builder.
 *
 * Usage:
 * ```html
 * <app-field-type-selector
 *   [field]="field()"
 *   [isOpen]="isNew()"
 *   (onFieldTypeSelected)="handleFieldTypeChanged($event)"
 * />
 * ```
 */
@Component({
  selector: 'app-field-type-selector',
  standalone: true,
  imports: [CommonModule, AccordionComponent, RadioComponent, TranslatePipe, FormsModule],
  templateUrl: './field-type-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './field-type-selector.component.css',
})
export class FieldTypeSelectorComponent implements OnChanges {
  @Input() field!: FieldDefinition | null;
  @Input() isOpen = false;
  @Output() onFieldTypeSelected = new EventEmitter<string>();
  public selectedTypeName = 'unknown';
  public selectedFieldType: string | null = null;

  // Available field types loaded from centralized configuration
  // Uses getAvailableFieldTypes() to ensure only editable field types are shown
  availableFieldTypes = getAvailableFieldTypes();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] && this.field && this.field.type) {
      this.selectedTypeName =
        this.availableFieldTypes.find((ft) => ft.value === this.field?.type)?.label || 'unknown';
      this.selectedFieldType = this.field.type;
    }
  }

  onTypeSelected(newType: string): void {
    if (newType !== this.field?.type) {
      this.onFieldTypeSelected.emit(newType);
    }
  }
}
